import type { LineVector, ViewAngles } from '../dimension';
import type { Size } from '../util';
import { Vector } from '../vector';

export const EPSILON = 2.220446049250313e-16;

export const POINTS = {
	TOP_LEFT: 0,
	TOP_RIGHT: 1,
	BOTTOM_RIGHT: 2,
	BOTTOM_LEFT: 3,
};

export function linePlaneIntersection(line: LineVector, planeCenter: Vector, planeNormal: Vector): Vector {
	var lineDir = line.end.subtract(line.start).unit();
	var p0MinusL0 = planeCenter.subtract(line.start);

	var numerator = p0MinusL0.dot(planeNormal);
	var denominator = lineDir.dot(planeNormal);

	var ratio = numerator / denominator;
	var lineSegment = lineDir.multiply(ratio);

	return line.start.add(lineSegment);
}

export function lineIntersectionPoint(lineA: LineVector, lineB: LineVector) {
	let line1_start = lineA.start;
	let line1_end = lineA.end;

	let line2_start = lineB.start;
	let line2_end = lineB.end;

	let numeratorX =
		(line1_start.x * line1_end.y - line1_start.y * line1_end.x) * (line2_start.x - line2_end.x) -
		(line1_start.x - line1_end.x) * (line2_start.x * line2_end.y - line2_start.y * line2_end.x);
	let numeratorY =
		(line1_start.x * line1_end.y - line1_start.y * line1_end.x) * (line2_start.y - line2_end.y) -
		(line1_start.y - line1_end.y) * (line2_start.x * line2_end.y - line2_start.y * line2_end.x);
	let denominator =
		(line1_start.x - line1_end.x) * (line2_start.y - line2_end.y) -
		(line1_start.y - line1_end.y) * (line2_start.x - line2_end.x);

	if (denominator !== 0) {
		return new Vector(numeratorX / denominator, numeratorY / denominator);
	}

	return null;
}

export function estimateDimensions(quad: Vector[]) {
	let dimensions = new Vector();

	let estimatedWidth1 = quad[POINTS.BOTTOM_LEFT].subtract(quad[POINTS.BOTTOM_RIGHT]).length();
	let estimatedWidth2 = quad[POINTS.TOP_LEFT].subtract(quad[POINTS.TOP_RIGHT]).length();
	let estimatedHeight1 = quad[POINTS.BOTTOM_LEFT].subtract(quad[POINTS.TOP_LEFT]).length();
	let estimatedHeight2 = quad[POINTS.BOTTOM_RIGHT].subtract(quad[POINTS.TOP_RIGHT]).length();

	dimensions.x = (estimatedWidth1 + estimatedWidth2) / 2.0;
	dimensions.y = (estimatedHeight1 + estimatedHeight2) / 2.0;

	return dimensions;
}

export function calculateFocalLength(resolution: Size, viewAngle: ViewAngles) {
	var largestViewAngle = Math.max(viewAngle.horizontal, viewAngle.vertical);
	var largestLength = Math.max(resolution.width, resolution.height);

	var hvaTanHalf = Math.tan(largestViewAngle / 2.0);
	return largestLength / 2.0 / hvaTanHalf;
}
